import React, { useState, useEffect } from "react";
import { useTheme } from "styled-components";
import axios from "axios";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { useAuth } from "../../../contexts/AuthContext";
import { useProfileSetup } from "../../../contexts/ProfileContext";
import { fetchAuthSession } from "@aws-amplify/auth";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";

const INDUSTRY_OPTIONS = [
  "Marketing",
  "Technology",
  "Healthcare",
  "Finance",
  "Real Estate",
  "Manufacturing",
  "Retail",
  "Education",
  "Transportation",
  "Energy",
  "Construction",
  "Hospitality",
  "Entertainment",
  "Media",
  "Legal",
  "Agriculture",
  "Telecommunications",
  "Insurance",
  "Aerospace",
  "Automotive",
  "Food & Beverage",
  "Pharmaceuticals",
  "Biotechnology",
  "Logistics & Supply Chain",
  "Nonprofit",
  "Fashion & Apparel",
  "Beauty & Personal Care",
  "Sports & Recreation",
  "Government & Public Sector",
  "Consulting",
  "Environmental Services",
  "Mining & Metals",
  "Security & Defense",
  "Travel & Tourism",
  "Consumer Electronics",
  "Other",
];

const BUSINESS_TYPE_OPTIONS = [
  "B2B (Business-to-Business)",
  "B2C (Business-to-Consumer)",
  "B2C/Ecommerce",
  "B2B/B2C",
  "B2B/Ecommerce",
  "B2B/SaaS",
  "B2C/SaaS",
  "D2C (Direct-to-Consumer)",
  "B2G (Business-to-Government)",
  "E-commerce",
];

const EMPLOYEE_SIZE_OPTIONS = [
  "Self-employed",
  "1-10 employees",
  "11-50 employees",
  "51-200 employees",
  "201-500 employees",
  "501-1000 employees",
  "10001-5000 employees",
  "5000+ employees",
];

const REVENUE_OPTIONS = [
  "$0 – $100K",
  "$100K – $1M",
  "$1M – $10M",
  "$10M – $25M",
  "$25M – $50M",
  "$50M – $100M",
  "$100M – $250M",
  "$250M – $500M",
  "$500M – $1B",
  "$1B+",
];

export default function AccountView() {
   const theme = useTheme();
   const { user } = useAuth();
   const apiBase = process.env.REACT_APP_API_URL || "http://localhost:3001";

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    jobTitle: "",
    companyName: "",
    website: "",
    industry: "",
    businessType: "",
    targetAudience: "",
    employeeSize: "",
    revenue: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);

  
  const { profileData, setProfileData, profileLabels, getAuthHeaders, loading } = useProfileSetup();

  useEffect(() => {
    if (profileData && !loading) {
      setFormData(profileData);
    }
  }, [profileData, loading]);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const nextErrors = {};

    const emailRegex = /[^\s@]+@[^\s@]+\.[^\s@]+/;
    const countDigits = (s) => (s.match(/\d/g) || []).length;

    if (!formData.firstName.trim()) nextErrors.firstName = `${profileLabels.firstName} is required`;
    if (!formData.lastName.trim()) nextErrors.lastName = `${profileLabels.lastName} is required`;
    if (!formData.email.trim()) nextErrors.email = `${profileLabels.email} is required`;
    else if (!emailRegex.test(formData.email.trim())) nextErrors.email = "Enter a valid email";

    if (!formData.phone.trim()) nextErrors.phone = `${profileLabels.phone} is required`;
    else if (countDigits(formData.phone) < 7) nextErrors.phone = "Enter a valid phone number";

    if (!formData.industry) nextErrors.industry = `${profileLabels.industry} is required`;
    if (!formData.businessType) nextErrors.businessType = `${profileLabels.businessType} is required`;
    if (!formData.targetAudience.trim()) nextErrors.targetAudience = `${profileLabels.targetAudience} is required`;
    if (!formData.employeeSize) nextErrors.employeeSize = `${profileLabels.employeeSize} is required`;
    if (!formData.revenue) nextErrors.revenue = `${profileLabels.revenue} is required`;

    if (formData.website.trim()) {
      try {
        // eslint-disable-next-line no-new
        new URL(formData.website.trim().startsWith("http") ? formData.website.trim() : `https://${formData.website.trim()}`);
      } catch (_) {
        nextErrors.website = "Enter a valid website URL";
      }
    }

    return nextErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitMessage(null);
    setSubmitting(true);
    try {
      const userId = user?.userId || (typeof window !== "undefined" && localStorage.getItem("mb_userId"));
      if (!userId) {
        throw new Error("Missing userId");
      }

      const headers = await getAuthHeaders();
      await axios.put(`${apiBase}/users/${userId}`, formData, {headers});
      setProfileData(formData);
      setSubmitMessage("Saved");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to save account info", err);
      setSubmitMessage(err?.response?.data?.error || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[40vh]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="pt-8">
      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="firstName">{profileLabels.firstName} <span style={{ color: theme.colors.error }}>*</span></Label>
            <Input
              id="firstName"
              value={formData.firstName}
              onChange={(e) => updateField("firstName", e.target.value)}
              required
            />
            {errors.firstName && <p className="text-sm" style={{ color: theme.colors.error }}>{errors.firstName}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="lastName">{profileLabels.lastName} <span style={{ color: theme.colors.error }}>*</span></Label>
            <Input
              id="lastName"
              value={formData.lastName}
              onChange={(e) => updateField("lastName", e.target.value)}
              required
            />
            {errors.lastName && <p className="text-sm" style={{ color: theme.colors.error }}>{errors.lastName}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="email">{profileLabels.email} <span style={{ color: theme.colors.error }}>*</span></Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => updateField("email", e.target.value)}
              required
            />
            {errors.email && <p className="text-sm" style={{ color: theme.colors.error }}>{errors.email}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="phone">{profileLabels.phone} <span style={{ color: theme.colors.error }}>*</span></Label>
            <Input
              id="phone"
              type="tel"
              placeholder="e.g., +1 (555) 123-4567"
              value={formData.phone}
              onChange={(e) => updateField("phone", e.target.value)}
              required
            />
            {errors.phone && <p className="text-sm" style={{ color: theme.colors.error }}>{errors.phone}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="jobTitle">{profileLabels.jobTitle}</Label>
            <Input
              id="jobTitle"
              placeholder="e.g., CEO, VP of Marketing"
              value={formData.jobTitle}
              onChange={(e) => updateField("jobTitle", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="companyName">{profileLabels.companyName}</Label>
            <Input
              id="companyName"
              value={formData.companyName}
              onChange={(e) => updateField("companyName", e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="website">{profileLabels.website}</Label>
          <Input
            id="website"
            placeholder="example.com or https://example.com"
            value={formData.website}
            onChange={(e) => updateField("website", e.target.value)}
          />
          {errors.website && <p className="text-sm" style={{ color: theme.colors.error }}>{errors.website}</p>}
        </div>

        <div className="space-y-1">
          <Label htmlFor="industry">{profileLabels.industry} <span style={{ color: theme.colors.error }}>*</span></Label>
          <select
            id="industry"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            value={formData.industry}
            onChange={(e) => updateField("industry", e.target.value)}
            required
          >
            <option value="" disabled>
              Select an industry
            </option>
            {INDUSTRY_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          {errors.industry && <p className="text-sm" style={{ color: theme.colors.error }}>{errors.industry}</p>}
        </div>

        <div className="space-y-1">
          <Label htmlFor="businessType">{profileLabels.businessType} <span style={{ color: theme.colors.error }}>*</span></Label>
          <select
            id="businessType"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            value={formData.businessType}
            onChange={(e) => updateField("businessType", e.target.value)}
            required
          >
            <option value="" disabled>
              Select a business type
            </option>
            {BUSINESS_TYPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          {errors.businessType && <p className="text-sm" style={{ color: theme.colors.error }}>{errors.businessType}</p>}
        </div>

        <div className="space-y-1">
          <Label htmlFor="targetAudience">{profileLabels.targetAudience}? Details <span style={{ color: theme.colors.error }}>*</span></Label>
          <textarea
            id="targetAudience"
            rows={4}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            placeholder="Demographics, location, interests, etc."
            value={formData.targetAudience}
            onChange={(e) => updateField("targetAudience", e.target.value)}
            required
          />
          {errors.targetAudience && <p className="text-sm" style={{ color: theme.colors.error }}>{errors.targetAudience}</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="employeeSize">{profileLabels.employeeSize} <span style={{ color: theme.colors.error }}>*</span></Label>
            <select
              id="employeeSize"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={formData.employeeSize}
              onChange={(e) => updateField("employeeSize", e.target.value)}
              required
            >
              <option value="" disabled>
                Select employee size
              </option>
              {EMPLOYEE_SIZE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {errors.employeeSize && <p className="text-sm" style={{ color: theme.colors.error }}>{errors.employeeSize}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="revenue">{profileLabels.revenue} <span style={{ color: theme.colors.error }}>*</span></Label>
            <select
              id="revenue"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={formData.revenue}
              onChange={(e) => updateField("revenue", e.target.value)}
              required
            >
              <option value="" disabled>
                Select revenue range
              </option>
              {REVENUE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {errors.revenue && <p className="text-sm" style={{ color: theme.colors.error }}>{errors.revenue}</p>}
          </div>
        </div>

        <div className="pt-2 space-y-2">
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "Saving..." : "Save"}
          </Button>
          {submitMessage && (
            <p className="text-sm text-muted-foreground">{submitMessage}</p>
          )}
        </div>
      </form>
    </div>
  );
}


